<template>
  <component
    :is="linkTag"
    :href="!disabled ? resolvedHref : null"
    :target="target || field.value.target"
    :class="[
      'link-fi',
      `fi-text-size-df-${size}`,
      disabled ? 'link-fi--disabled' : '',
      primary ? 'link-fi--primary' : '',
      secondary ? 'link-fi--secondary' : '',
      textLink ? 'link-fi--text-link' : '',
      thin ? 'link-fi--thin' : '',
      reverse ? 'link-fi--reverse' : '',
      theme ? `link-fi--theme-${theme}` : '',
    ]"
    :rel="getRel"
    :data-test="dataTest"
    :data-test-2="dataTest2"
    :data-test-3="dataTest3"
    @click="click"
    @mousedown="mousedown"
    @animationend="activeAnimationEnd"
  >
    <template v-if="reverse">
      <div
        v-if="!textLink"
        :class="`fi-text-size-df-${size} link-fi__arrow`"
      >
        <arrow-icon />
      </div>
      <span
        v-for="ripple in Object.keys(ripples)"
        :key="ripple"
        :style="ripples[ripple].style"
        :class="ripples[ripple].cssClass"
        class="link-fi__ripple"
        @animationend="(evt) => animationEnd(evt, ripple)"
      />
      <slot>
        {{ slotDefaultText }}
      </slot>
    </template>
    <template v-else>
      <slot>
        {{ slotDefaultText }}
      </slot>
      <div
        v-if="!textLink"
        :class="`fi-text-size-df-${size} link-fi__arrow`"
      >
        <arrow-icon />
      </div>
      <span
        v-for="ripple in Object.keys(ripples)"
        :key="ripple"
        :style="ripples[ripple].style"
        :class="ripples[ripple].cssClass"
        class="link-fi__ripple"
        @animationend="(evt) => animationEnd(evt, ripple)"
      />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';

import ArrowIcon from '@/components/atoms/arrow-icon/ArrowIcon.vue';
import useJssFormsConditionalField from '@/composables/jssFormsConditionalField';

defineOptions({ name: 'LinkFi' });

interface Props {
  field?: Record<string, any>;
  href?: string | null;
  target?: string | null;
  size?: number;
  primary?: boolean;
  secondary?: boolean;
  textLink?: boolean;
  thin?: boolean;
  reverse?: boolean;
  rel?: string | null;
  tag?: string;
  external?: boolean;
  disabled?: boolean;
  theme?: string | null;
  dataTest?: string | null;
  dataTest2?: string | null;
  dataTest3?: string | null;
  dataFormElementKey?: string;
  dataFormItemId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  field: () => ({ value: {} }),
  href: null,
  target: null,
  size: 18,
  primary: false,
  secondary: false,
  textLink: false,
  thin: false,
  reverse: false,
  rel: null,
  tag: 'a',
  external: false,
  disabled: false,
  theme: null,
  dataTest: null,
  dataTest2: null,
  dataTest3: null,
  dataFormElementKey: undefined,
  dataFormItemId: undefined,
});

const emit = defineEmits<{ 'link-click': [event: MouseEvent, href: string | null] }>();

useJssFormsConditionalField(props);

const ripples = reactive<Record<string, { style: string; cssClass: string | null }>>({});
const rippleForActive = ref<string | null>(null);
const isActive = ref(false);

const resolvedHref = computed(() => props.href || props.field.value.href);

const slotDefaultText = computed(
  () => props.field.value.text || props.href || props.field.value.href,
);

const getRel = computed(() => {
  const rels: string[] = [];

  if (props.rel) {
    rels.push(props.rel);
  }

  if (
    (props.external && props.target === '_blank') ||
    (props.field.value.url && props.field.value.target === '_blank')
  ) {
    rels.push('noopener');
    rels.push('noreferrer');
  }

  return rels.length ? rels.join(' ') : null;
});

const linkTag = computed(() => {
  if (props.disabled) {
    return 'span';
  }
  return props.tag;
});

const click = (event: MouseEvent) => {
  mousedown(event);
  emit('link-click', event, resolvedHref.value);
};

const mousedown = ({ type, layerX, layerY }: MouseEvent) => {
  if (type !== 'mousedown') {
    return;
  }
  if (Object.keys(ripples).some((ripple) => !ripples[ripple].cssClass)) {
    return;
  }
  const style = `left: calc(-50% + ${layerX}px); top: calc(-50% + ${layerY}px);`;
  ripples['timeStamp'] = {
    style,
    cssClass: null,
  };
};

const animationEnd = (evt: AnimationEvent, ripple: string) => {
  if (evt.animationName.startsWith('scaleOut')) {
    delete ripples[ripple];
    return;
  }
  if (rippleForActive.value || !isActive.value) {
    ripples[ripple].cssClass = 'scaling-out';
    return;
  }
  rippleForActive.value = ripple;
};

const activeAnimationEnd = ({ animationName }: AnimationEvent) => {
  if (animationName.startsWith('active')) {
    isActive.value = true;
  } else if (animationName.startsWith('inactive')) {
    isActive.value = false;
    if (rippleForActive.value) {
      ripples[rippleForActive.value].cssClass = 'scaling-out';
      rippleForActive.value = null;
    }
  }
};
</script>

<style scoped src="./link-fi" />
